import { useMemo, useState } from "react";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import TextButton from "@shared/v2/TextButton";
import TextToggle from "@shared/v2/TextToggle";
import Typography from "@shared/v2/Typography";
import { CheckSolidV6, ChevronDown, TurnDownRightSolidV6 } from "@shared/v2/Icomoon";
import { useWizardContext } from "./WizardContext";

const OPTIONS = [
  {
    value: "monthly",
    label: "Monthly",
  },
  {
    value: "yearly",
    label: "Yearly",
  },
];

const getNumber = (value) => Number(value) || 0;

const calculateRatioTotal = (baseValue, numerator, denominator) => {
  if (!baseValue || !numerator || !denominator) return 0;
  return Math.ceil(baseValue * (numerator / denominator));
};

const calculatePeriodValue = (yearlyValue, period, workingMonths) => {
  if (period === "yearly") return yearlyValue;
  if (!workingMonths) return yearlyValue;
  return Math.ceil(yearlyValue / workingMonths);
};

const formatCount = (value) => getNumber(value).toLocaleString();

const ActivityGoals = () => {
  const {
    goToPage,
    year,
    wizardData: { goals, actions },
    completeWizard,
  } = useWizardContext();
  const [toggle, setToggle] = useState("monthly");
  const [showCallBreakdown, setShowCallBreakdown] = useState(true);
  const [isSavingGoals, setIsSavingGoals] = useState(false);

  const activityGoals = useMemo(() => {
    const workingMonths = 12;
    const unitGoal = getNumber(goals.closedUnits);
    const unitSources = goals.unitSources || {};

    const personalProductivityStats = goals.personalProductivityStats || {};
    const personalClosingConversionStats = goals.personalClosingConversionStats || {};

    const getProductivityStat = (key) => getNumber(personalProductivityStats[key] ?? goals[key]);

    const getClosingConversionStat = (key) => getNumber(personalClosingConversionStats[key] ?? goals[key]);

    const callsFromSphereYearly =
      getNumber(unitSources.pastClientsSphereReferrals) * getClosingConversionStat("callsPerClosed");
    const callsFromWebSignTeamYearly =
      getNumber(unitSources.brivityWebLeadsSignCallsTeamLeads) *
      getClosingConversionStat("callsWebSignTeamPerClosed");
    const callsYearly = callsFromSphereYearly + callsFromWebSignTeamYearly;

    const unitsClosedLastTwelveMonths = getProductivityStat("unitsClosedLastTwelveMonths");

    const appointmentsSetYearly = calculateRatioTotal(
      unitGoal,
      getProductivityStat("appointmentsSetLastTwelveMonths"),
      unitsClosedLastTwelveMonths,
    );
    const appointmentsHeldYearly = calculateRatioTotal(
      unitGoal,
      getProductivityStat("appointmentsHeldLastTwelveMonths"),
      unitsClosedLastTwelveMonths,
    );
    const contactsYearly = calculateRatioTotal(
      callsYearly,
      getProductivityStat("contactsLastTwelveMonths"),
      getProductivityStat("callsLastTwelveMonths"),
    );
    const signingsYearly = calculateRatioTotal(
      unitGoal,
      getProductivityStat("signingsLastTwelveMonths"),
      unitsClosedLastTwelveMonths,
    );
    const openHousesYearly =
      getClosingConversionStat("openHousesPerClosed") * getNumber(unitSources.openHouses);
    const doorKnocksYearly =
      getClosingConversionStat("doorKnocksPerClosed") * getNumber(unitSources.doorKnocking);
    const circleProspectingCallsYearly =
      getClosingConversionStat("callsCirclePerClosed") * getNumber(unitSources.circleProspecting);
    const canceledExpiredCallsYearly =
      getClosingConversionStat("callsCanceledPerClosed") * getNumber(unitSources.canceledExpired);
    const fsboCallsYearly = getClosingConversionStat("callsFsboPerClosed") * getNumber(unitSources.fsbo);

    const yearlyStats = {
      calls: callsYearly,
      callsFromSphere: callsFromSphereYearly,
      callsFromWebSignTeam: callsFromWebSignTeamYearly,
      contacts: contactsYearly,
      appointmentsSet: appointmentsSetYearly,
      appointmentsHeld: appointmentsHeldYearly,
      signings: signingsYearly,
      openHouses: openHousesYearly,
      doorKnocks: doorKnocksYearly,
      circleProspectingCalls: circleProspectingCallsYearly,
      canceledExpiredCalls: canceledExpiredCallsYearly,
      fsboCalls: fsboCallsYearly,
    };

    const displayCallsFromSphere = calculatePeriodValue(yearlyStats.callsFromSphere, toggle, workingMonths);
    const displayCallsFromWebSignTeam = calculatePeriodValue(
      yearlyStats.callsFromWebSignTeam,
      toggle,
      workingMonths,
    );

    return {
      currentNetIncomeGoal: getNumber(goals.currentNetIncomeGoal),
      unitGoal,
      yearlyStats,
      displayStats: {
        calls: displayCallsFromSphere + displayCallsFromWebSignTeam,
        callsFromSphere: displayCallsFromSphere,
        callsFromWebSignTeam: displayCallsFromWebSignTeam,
        contacts: calculatePeriodValue(yearlyStats.contacts, toggle, workingMonths),
        appointmentsSet: calculatePeriodValue(yearlyStats.appointmentsSet, toggle, workingMonths),
        appointmentsHeld: calculatePeriodValue(yearlyStats.appointmentsHeld, toggle, workingMonths),
        signings: calculatePeriodValue(yearlyStats.signings, toggle, workingMonths),
        openHouses: calculatePeriodValue(yearlyStats.openHouses, toggle, workingMonths),
        doorKnocks: calculatePeriodValue(yearlyStats.doorKnocks, toggle, workingMonths),
        circleProspectingCalls: calculatePeriodValue(
          yearlyStats.circleProspectingCalls,
          toggle,
          workingMonths,
        ),
        canceledExpiredCalls: calculatePeriodValue(yearlyStats.canceledExpiredCalls, toggle, workingMonths),
        fsboCalls: calculatePeriodValue(yearlyStats.fsboCalls, toggle, workingMonths),
      },
    };
  }, [goals, toggle]);

  const handleFinishAndSave = async () => {
    const activityGoalsToSave = {
      ...goals,
      ...activityGoals.yearlyStats,
      brivityActivityGoals: {
        calls: activityGoals.yearlyStats.calls,
        callsFromSphere: activityGoals.yearlyStats.callsFromSphere,
        callsFromWebSignTeam: activityGoals.yearlyStats.callsFromWebSignTeam,
        contacts: activityGoals.yearlyStats.contacts,
        appointmentsSet: activityGoals.yearlyStats.appointmentsSet,
        appointmentsHeld: activityGoals.yearlyStats.appointmentsHeld,
        signings: activityGoals.yearlyStats.signings,
        openHouses: activityGoals.yearlyStats.openHouses,
      },
      loggedActivityGoals: {
        doorKnocks: activityGoals.yearlyStats.doorKnocks,
        circleProspectingCalls: activityGoals.yearlyStats.circleProspectingCalls,
        canceledExpiredCalls: activityGoals.yearlyStats.canceledExpiredCalls,
        fsboCalls: activityGoals.yearlyStats.fsboCalls,
      },
    };

    setIsSavingGoals(true);
    actions?.setLocalGoals(activityGoalsToSave);
    const isSaved = await actions?.saveGoals?.(activityGoalsToSave);
    setIsSavingGoals(false);

    if (isSaved === false) return;
    completeWizard({ nextGoals: activityGoalsToSave });
  };

  return (
    <>
      <Modal.Header
        title={
          <>
            {`${year} Business Planning`} <span className="tw-text-12d tw-text-gray-50">Step 5 of 5</span>
          </>
        }
        closeButton
      />
      <Modal.Body className="tw-flex tw-flex-col tw-gap-[16px] tw-overflow-y-auto">
        <div className="tw-flex tw-justify-between tw-items-end">
          <div className="tw-flex tw-items-center tw-gap-[8px]">
            <Typography variant="subtitle-m-bold">Activity Goals</Typography>
            <TextToggle
              size="small"
              options={OPTIONS}
              value={toggle}
              onChange={(e) => setToggle(e.target.value)}
            />
          </div>
        </div>
        <div className="tw-border-solid tw-border-[1px] tw-border-gray-10 tw-rounded-[8px] tw-p-[16px] tw-text-center">
          Complete these activities to close <b className="tw-text-18d">{activityGoals.unitGoal} units</b> and
          make <b className="tw-text-18d">${activityGoals.currentNetIncomeGoal.toLocaleString()}</b> in {year}
        </div>
        <div className="tw-flex tw-gap-[16px]">
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-rounded-[8px] tw-p-[16px]">
            <div className="tw-text-14d tw-font-bold">Brivity Activities</div>
            <div>
              <div className="tw-flex tw-justify-between tw-items-center tw-pb-[4px]">
                <div className="tw-flex-1">
                  <b className="tw-text-18d">{formatCount(activityGoals.displayStats.calls)}</b> calls
                </div>
                <div className="tw-flex-1">
                  <TextButton
                    className="tw-whitespace-nowrap"
                    onClick={() => setShowCallBreakdown((current) => !current)}
                  >
                    {showCallBreakdown ? "Hide" : "Show"} Call Breakdown{" "}
                    <ChevronDown
                      size="s"
                      className={`tw-transition-transform ${showCallBreakdown ? "tw-rotate-180" : ""}`}
                    />
                  </TextButton>
                </div>
              </div>
              {showCallBreakdown && (
                <>
                  <div className="tw-flex tw-justify-between tw-items-center tw-py-[4px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-x-0 tw-border-t-0">
                    <div className="tw-flex-1">
                      <TurnDownRightSolidV6 size="s" />{" "}
                      {formatCount(activityGoals.displayStats.callsFromSphere)}
                    </div>
                    <div className="tw-text-12d">Sphere, Past Clients, Referrals</div>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-center tw-py-[4px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-x-0 tw-border-t-0">
                    <div className="tw-flex-1">
                      <TurnDownRightSolidV6 size="s" />{" "}
                      {formatCount(activityGoals.displayStats.callsFromWebSignTeam)}
                    </div>
                    <div className="tw-text-12d">Brivity/Web Leads, Signs, Team Leads</div>
                  </div>
                </>
              )}
            </div>
            <div className="tw-flex tw-justify-between">
              <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px]">
                <div>
                  <b className="tw-text-18d">{formatCount(activityGoals.displayStats.contacts)}</b> contacts
                </div>
                <div>
                  <b className="tw-text-18d">{formatCount(activityGoals.displayStats.appointmentsHeld)}</b>{" "}
                  appts. held
                </div>
                <div>
                  <b className="tw-text-18d">{formatCount(activityGoals.displayStats.openHouses)}</b>
                  <span> open houses</span>
                </div>
              </div>
              <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px]">
                <div>
                  <b className="tw-text-18d">{formatCount(activityGoals.displayStats.appointmentsSet)}</b>{" "}
                  appts. set
                </div>
                <div>
                  <b className="tw-text-18d">{formatCount(activityGoals.displayStats.signings)}</b> signings
                </div>
              </div>
            </div>
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[12px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-rounded-[8px] tw-p-[16px]">
            <div className="tw-text-14d tw-font-bold">Logged Activities</div>
            <div>
              Knock on <b>{formatCount(activityGoals.displayStats.doorKnocks)}</b> doors
            </div>
            <div>
              Make <b>{formatCount(activityGoals.displayStats.circleProspectingCalls)}</b> circle prospecting
              calls
            </div>
            <div>
              Make <b>{formatCount(activityGoals.displayStats.canceledExpiredCalls)}</b> canceled/expired
              calls
            </div>
            <div>
              Make <b>{formatCount(activityGoals.displayStats.fsboCalls)}</b> FSBO calls
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        <Button schema="tertiary" size="medium" onClick={() => goToPage(6)}>
          Back
        </Button>
        <Button
          size="medium"
          className="tw-inline-flex tw-items-center tw-gap-[6px]"
          onClick={handleFinishAndSave}
          isLoading={isSavingGoals}
          disabled={isSavingGoals}
        >
          Finish & Save Goals <CheckSolidV6 />
        </Button>
      </Modal.Footer>
    </>
  );
};

export default ActivityGoals;
